import { createContext, type MouseEvent, type ReactNode, useContext, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"

import { ReactComponent as IconWarning } from "assets/svg/exclamation-triangle.svg"

import { ModalLayout } from "layouts/ModalLayout"
import { ButtonNew } from "components/form/ButtonNew"

import type { AccountSettingsTab } from "./config"

import styles from "./index.module.scss"

type AccountSettingsTabGuard = {
  isDirty: boolean
  isSaving?: boolean
  onSave?: () => Promise<boolean> | boolean
}

type AccountSettingsTabGuardContextValue = {
  setGuard: (guard: AccountSettingsTabGuard | null) => void
}

const AccountSettingsTabGuardContext = createContext<AccountSettingsTabGuardContextValue>({
  setGuard: () => undefined,
})

export const useAccountSettingsTabGuard = (guard: AccountSettingsTabGuard | null) => {
  const { setGuard } = useContext(AccountSettingsTabGuardContext)

  useEffect(() => {
    setGuard(guard)

    return () => {
      setGuard(null)
    }
  }, [guard?.isDirty, guard?.isSaving, guard?.onSave, setGuard])
}

const UnsavedChangesModal = ({
  currentTabLabel,
  nextTabLabel,
  isSaving,
  onClose,
  onDiscard,
  onSave,
}: {
  currentTabLabel: string
  nextTabLabel: string
  isSaving: boolean
  onClose: () => void
  onDiscard: () => void
  onSave: () => void
}) =>
  createPortal(
    <ModalLayout width="480px" isWithoutFooter onClose={onClose} className={styles.unsavedChangesModal}>
      <div className={styles.unsavedChangesContent}>
        <div className={styles.unsavedChangesIcon}>
          <IconWarning />
        </div>
        <h2 className={styles.unsavedChangesTitle}>Unsaved changes</h2>
        <p className={styles.unsavedChangesMessage}>
          You have unsaved changes on <strong>{currentTabLabel}</strong>. What would you like to do before switching to{" "}
          <strong>{nextTabLabel}</strong>?
        </p>
      </div>
      <div className={styles.unsavedChangesFooter}>
        <ButtonNew color="redBordered" size="halfWidth" onClick={onDiscard} disabled={isSaving}>
          Discard
        </ButtonNew>
        <ButtonNew color="blue" size="halfWidth" onClick={onSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save changes"}
        </ButtonNew>
      </div>
    </ModalLayout>,
    document.body,
  )

export const AccountSettingsTabGuardProvider = ({
  tabs,
  children,
}: {
  tabs: AccountSettingsTab[]
  children: (handleTabClick: (event: MouseEvent<HTMLAnchorElement>, tab: AccountSettingsTab) => void) => ReactNode
}) => {
  const [guard, setGuard] = useState<AccountSettingsTabGuard | null>(null)
  const [pendingTab, setPendingTab] = useState<AccountSettingsTab | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const currentTab = useMemo(() => tabs.find(tab => tab.path === location.pathname), [location.pathname, tabs])

  const handleTabClick = (event: MouseEvent<HTMLAnchorElement>, tab: AccountSettingsTab) => {
    if (tab.path === location.pathname || !guard?.isDirty) return

    event.preventDefault()
    setPendingTab(tab)
  }

  const closeModal = () => {
    setPendingTab(null)
  }

  const navigateToPendingTab = () => {
    if (!pendingTab) return

    const nextPath = pendingTab.path
    setPendingTab(null)
    navigate(nextPath)
  }

  const handleDiscard = () => {
    navigateToPendingTab()
  }

  const handleSaveChanges = async () => {
    if (!guard?.onSave) {
      navigateToPendingTab()
      return
    }

    const result = await guard.onSave()

    if (result) {
      navigateToPendingTab()
    }
  }

  return (
    <AccountSettingsTabGuardContext.Provider value={{ setGuard }}>
      {children(handleTabClick)}
      {pendingTab && currentTab && (
        <UnsavedChangesModal
          currentTabLabel={currentTab.label}
          nextTabLabel={pendingTab.label}
          isSaving={Boolean(guard?.isSaving)}
          onClose={closeModal}
          onDiscard={handleDiscard}
          onSave={handleSaveChanges}
        />
      )}
    </AccountSettingsTabGuardContext.Provider>
  )
}
